<template>
    <div class="contract-form">
        <!-- Contract -->
        <h5 v-text="'Dane umowy'" class="font-weight-medium mb-3" />
        <div class="row">
            <!-- TEMP HIDDEN: contractId input -->
            <!-- <the-input
                :text="contract.contractId"
                class="col-md-4"
                label="Numer umowy"
                @input="contract.contractId = $event"
            /> -->
            <the-input
                required
                class="cux-exclude col-md-4 text-primary"
                label="Numer punktu zbiórki"
                :tooltip="cpIdTootlip"
                :text="contract.collectionPointId"
                :placeholder="cpIdPlaceholder"
                :maxlength="cpIdMaxLength"
                :invalid="contract.invalidCollectionPointId"
                :invalid-feedback="
                    `Niepoprawny format identyfikatora sklepu
                    (musi być w formacie ${cpIdPlaceholder})`"
                :validation="formatCpId"
                @input="onCpIdInput"
            />
            <the-input
                v-if="showRetailerToken"
                required
                class="col-md-4 text-primary"
                label="Token operatora"
                :tooltip="retailerTokenTootlip"
                :text="contract.retailerToken"
                @input="onRetailerTokenInput"
            />
        </div>

        <div class="row">
            <date-input
                required
                :val="contract.startDate"
                :id="`startDate-${id}`"
                class="col-md-4"
                placeholder="Data zawarcia umowy"
                :lock-future-days="[contract.endDate]"
                @change="contract.startDate = $event"
            />
            <date-input
                required
                :val="contract.endDate"
                :id="`endDate-${id}`"
                class="col-md-4"
                placeholder="Data zakończenia umowy"
                :disabled="contract.indefinite"
                :lock-past-days="[$moment().format('YYYY-MM-DD'), contract.startDate]"
                @change="contract.endDate = $event"
            />
            <div class="col-md-4 d-flex align-items-center">
                <ui-checkbox-input
                    v-model="contract.indefinite"
                    name="indefinite"
                    class="text-primary mb-2"
                    label="Umowa na czas nieokreślony"
                    @change="val => { if (val && contract.endDate) contract.endDate = '' }"
                />
            </div>
        </div>

        <!-- Fractions -->
        <div v-if="!isLoading && contract.fractions">
            <h5 v-text="'Frakcje objęte umową'" class="mt-4 mb-3 font-weight-medium" />
            <!-- Fractions list header -->
            <div class="d-none d-md-flex justify-content-between mb-2">
                <span v-text="'Frakcja'" class="pl-3" />
                <span class="d-flex align-items-center">
                    Opłata za obsługę frakcji
                    <a-icon
                        v-tooltip="'Opłata, którą dany Operator wypłaci Ci za zbiórkę'"
                        class="ml-1"
                        height="16"
                        icon="Info"
                    />
                </span>
            </div>
            <!-- Fractions list -->
            <div
                v-for="fraction in contract.fractions"
                :key="fraction.id"
                class="card flex-column flex-md-row justify-content-between p-3 mb-2"
            >
                <div class="d-flex align-items-start align-items-md-center">
                    <a-switch
                        class="mr-2"
                        :id="`fraction-${fraction.id}`"
                        :checked="fraction.enabled"
                        @change="setFractionEnable($event, fraction)"
                    />
                    <fraction-label
                        show-details
                        show-details-tooltip
                        class="align-items-start align-items-md-center"
                        :fraction="fraction"
                        :disabled="!fraction.enabled"
                    />
                </div>
                <label
                    :for="`handlingFee-${fraction.id}`"
                    class="d-md-none d-flex text-sm mt-3"
                >
                    Opłata za obsługę frakcji
                    <a-icon
                        v-tooltip="'Opłata, którą dany Operator wypłaci Ci za zbiórkę'"
                        class="ml-1"
                        height="16"
                        icon="Info"
                    />
                </label>
                <money-input
                    class="m-0"
                    style="width: 7rem"
                    :id="`handlingFee-${fraction.id}`"
                    :price="+fraction.handlingFee"
                    :decimal-places="3"
                    :disabled="!fraction.enabled"
                    @onChange="fraction.handlingFee = $event"
                />
            </div>
        </div>
        <div v-else class="text-center">
            <page-preloader min-height="10rem" loading-status="loading" />
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import cloneDumb from '@/helpers/cloneDumb'
import { OPERATOR_CODES } from '@/utils/constants/drs/operators'

export default {
    data() {
        return {
            isLoading: false,
            contract: {}
        }
    },
    props: {
        id: { type: Number, required: true },
        selectedOperator: { type: Object, required: true },
        initialContract: {
            type: Object,
            default: () => ({
                contractId: '',
                collectionPointId: '',
                retailerToken: '',
                invalidCollectionPointId: false,
                startDate: '',
                endDate: '',
                indefinite: false,
                fractions: []
            })
        }
    },
    async created() {
        this.isLoading = true

        this.contract = cloneDumb(this.initialContract)
        this.contract.invalidCollectionPointId = !this.isValidCpId(this.contract.collectionPointId)

        // fetch fractions
        await this.fetchAndMapFractions()

        if (!this.contract.fractions?.length) {
            this.contract.fractions = this.getFractions.map(fr => ({
                ...fr,
                enabled: false,
                handlingFee: 0
            }))
        }

        this.isLoading = false
    },
    watch: {
        contract: {
            deep: true,
            handler(newVal) {
                this.$emit('on-change', newVal)
            }
        }
    },
    computed: {
        ...mapGetters('drsOperators', ['getFractions']),
        cpIdTootlip() {
            return `Numer punktu zbiórki to unikalny identyfikator Twojego sklepu.
                <br/>Znajdziesz go w Panelu Operatora.`
        },
        cpIdFormats() {
            const formats = {
                [OPERATOR_CODES.PSK]: ['R*******-00***'],
                [OPERATOR_CODES.KAUCJA_PL]: ['10*****-**', '10*****-*'],
                [OPERATOR_CODES.POLKA]: ['********-****-****-****-************']
            }
            return formats[this.selectedOperator.code] || []
        },
        cpIdPlaceholder() {
            return this.cpIdFormats.join(' lub ').replaceAll('*', 'x')
        },
        cpIdMaxLength() {
            return Math.max(...this.cpIdFormats.map(p => p.length))
        },
        retailerTokenTootlip() {
            return `Token to unikalny ciąg znaków, uwierzytelniający punkt zbiórki.
                <br/>Można go uzyskać, kontaktując się bezpośrednio z operatorem 
                (+48 455 455 221 lub <u>kontakt.pk@polskakaucja.pl</u>)`
        },
        showRetailerToken() {
            return this.selectedOperator.code === OPERATOR_CODES.POLKA
        }
    },
    methods: {
        ...mapActions('drsOperators', ['fetchAndMapFractions']),
        setFractionEnable(value, fraction) {
            fraction.enabled = value
            if (!value) {
                fraction.handlingFee = 0
            }
        },
        isValidCpId(val) {
            if (!this.cpIdFormats.length) return true

            return !this.contract.collectionPointId ||
                // check if matches any allowed format
                this.cpIdFormats.some(format => {
                    const regex = new RegExp('^' + format.replace(/\*/g, '[a-zA-Z0-9]') + '$')
                    return regex.test(val)
                })
        },
        formatCpId(val) {
            if (!this.cpIdFormats.length) return val

            const format = this.cpIdFormats[0]
            if (!format) return val

            // split format into groups by '-'
            const groups = format.split('-')

            let valIndex = 0
            const formattedGroups = []

            for (const group of groups) {
                let groupStr = ''
                for (let i = 0; i < group.length && valIndex < val.length;) {
                    const ch = val[valIndex]

                    // keep only alphanumeric characters
                    if (/[a-zA-Z0-9]/.test(ch)) {
                        // insert user char
                        groupStr += ch
                        valIndex++
                    } else {
                        // skip characters that are not allowed
                        valIndex++
                        continue
                    }

                    i++
                }
                if (groupStr.length) formattedGroups.push(groupStr)
            }

            // join the formatted groups with '-' and return
            return formattedGroups.join('-')
        },
        onCpIdInput(val) {
            const formatted = this.formatCpId(val)

            this.contract.collectionPointId = formatted
            this.contract.invalidCollectionPointId = !this.isValidCpId(formatted)
        },
        onRetailerTokenInput(val) {
            this.contract.retailerToken = val
        }
    }
}
</script>
<style lang="scss">
@import '@/styles/_custom.scss';
@import '~bootstrap/scss/mixins';
.contract-form {
     @include media-breakpoint-down(sm) {
        .a-switch {
            padding-top: 2px;
        }
    }
}
</style>